import type { NormalizedMarketRow } from '@/types/market';
import type { ScoreBand } from '@/types/insights';
import { clamp, pctChange } from '@/utils/numbers';
import { scoringWeights } from '@/config/scoringWeights';
import { magnitudeBand } from './bands';

/**
 * inventoryPressure: 0..100, higher = more supply pressure building
 * (favors buyers, market loosening). Built from the trend in months-of-
 * inventory and active listings for this geography, not cross-geography
 * z-scores -- this is about direction of travel, which matters as much as
 * the current level for a "which way is this market heading" read.
 */
export function computeInventoryPressure(row: NormalizedMarketRow): ScoreBand {
  const w = scoringWeights.inventoryPressure;
  const moiTrend = pctChange(row.monthsOfInventory, row.monthsOfInventoryPrevPeriod);
  const activeTrend = pctChange(row.activeListings, row.activeListingsPrevPeriod);

  if (moiTrend === undefined && activeTrend === undefined) return magnitudeBand(undefined);

  const parts: number[] = [];
  if (moiTrend !== undefined) parts.push(moiTrend * w.moiTrendWeight);
  if (activeTrend !== undefined) parts.push(activeTrend * w.activeListingsTrendWeight);

  const composite = parts.reduce((a, b) => a + b, 0);
  const scaled = clamp(50 + composite * w.scaleToRange, 0, 100);
  return magnitudeBand(scaled);
}
